/**
 * Centralized role name constants for the frontend RBAC system.
 * 
 * This module contains all role names used throughout the frontend application.
 * Use these constants instead of hardcoding role names to ensure consistency
 * and make future changes easier.
 * 
 * Role Hierarchy (by level):
 * - SUPER_ADMIN (level 100): Full system access
 * - BROKERAGE_ADMIN (level 75): Brokerage management  
 * - LIMITED_ADMIN (level 50): Limited admin functions
 * - AGENT (level 25): Real estate agent
 * - LAWYER (level 20): Legal professional
 * - CLIENT (level 1): End user/client
 */

export const RoleNames = {
  SUPER_ADMIN: "super_admin",
  BROKERAGE_ADMIN: "brokerage_admin", 
  LIMITED_ADMIN: "limited_admin",
  AGENT: "agent",
  LAWYER: "lawyer",
  CLIENT: "client"
};

export const ClientTypes = {
  BUYER: "Buyer",
  SELLER: "Seller"
};

// Role groups for easier checking
export const ADMIN_ROLES = new Set([
  RoleNames.SUPER_ADMIN,
  RoleNames.BROKERAGE_ADMIN,
  RoleNames.LIMITED_ADMIN
]);

export const STAFF_ROLES = new Set([
  RoleNames.SUPER_ADMIN,
  RoleNames.BROKERAGE_ADMIN,
  RoleNames.LIMITED_ADMIN,
  RoleNames.AGENT
]);

export const PROFESSIONAL_ROLES = new Set([
  RoleNames.AGENT,
  RoleNames.LAWYER
]);

export const ALL_ROLES = new Set([
  RoleNames.SUPER_ADMIN,
  RoleNames.BROKERAGE_ADMIN,
  RoleNames.LIMITED_ADMIN,
  RoleNames.AGENT,
  RoleNames.LAWYER,
  RoleNames.CLIENT
]);

/**
 * Helper functions for role checking
 */
export const hasRole = (user, roleName) => {
  // console.log("Checking role:", user?.roles);
  return user?.roles?.some(role => role.name === roleName) || 
         user?.primary_role?.name === roleName;
};

export const hasAnyRole = (user, roleNames) => {
  return roleNames.some(roleName => hasRole(user, roleName));
};

export const isAdmin = (user) => {
  return hasAnyRole(user, Array.from(ADMIN_ROLES));
};

export const isStaff = (user) => {
  return hasAnyRole(user, Array.from(STAFF_ROLES));
};

export const isProfessional = (user) => {
  return hasAnyRole(user, Array.from(PROFESSIONAL_ROLES));
};

export const isAgent = (user) => {
  return hasRole(user, RoleNames.AGENT);
};

export const isLawyer = (user) => {

  return hasRole(user, RoleNames.LAWYER);
};

export const isSuperAdmin = (user) => {
  return hasRole(user, RoleNames.SUPER_ADMIN);
};

export const isClient = (user) => {
  return hasRole(user, RoleNames.CLIENT);
};

/**
 * Professional role the user asked for at signup that KeyTa has not approved yet.
 * The RBAC role is only granted on approval, so this never implies permissions -
 * it only decides which dashboard shell (in its pending state) the user sees.
 * @returns {"agent"|"lawyer"|null}
 */
export const getPendingProfessionalRole = (user) => {
  const requested = user?.requested_role;
  if (!requested || !PROFESSIONAL_ROLES.has(requested)) return null;
  if (isProfessional(user) || isAdmin(user)) return null;
  return requested;
};

export default RoleNames;